import React, { useEffect, useState } from "react";
import {
  RouteProp,
  StackActions,
  useNavigation,
  useRoute,
  useFocusEffect,
} from "@react-navigation/native";
import { navigateToScreen } from "../../../utility/NavigationHelper";
import { Text, View, TouchableOpacity, ScrollView } from "react-native";
import {
  CreateResponsiveStyle,
  DEVICE_SIZES,
  useDeviceSize,
} from "rn-responsive-styles";
import { AppColors } from "../../../utility/AppColors";
import { MobileDeviceNavigationHeader } from "./Common/MobileDeviceNavigationHeader";
import { AppScreens } from "../../Utility/AppScreens";
import { AppFontStyle } from "../../../styles/AppFontStyle";
import Separator from "../../../Components/Separator";
import ImageComponent from "../../../Components/ImageComponent";
import { AppImages } from "../../../utility/AppImages";
import GettingStartedTabComponent from "../../../Components/help-common-components/GettingStartedTabComponent";
import HelpCenterMenuComponent from "../../../Components/help-common-components/HelpCenterMenuComponent";
import AccountManagementTabComponent from "../../../Components/help-common-components/AccountManagementTabComponent";
import TokenBeltsCoinsTabComponent from "../../../Components/help-common-components/TokenBeltsCoinsTabComponent";
import GiftTabComponent from "../../../Components/help-common-components/GiftTabComponent";
import EventParticipationTabComponent from "../../../Components/help-common-components/EventParticipationTabComponent";
import ContentModerationTabComponent from "../../../Components/help-common-components/ContentModerationTabComponent";
import DisputeCenterTabComponent from "../../../Components/help-common-components/DisputeCenterTabComponent";
import AudienceInteractionTabComponent from "../../../Components/help-common-components/AudienceInteractionTabComponent";
import RefundTabComponent from "../../../Components/help-common-components/RefundTabComponent";
import ContactUsTabComponent from "../../../Components/help-common-components/ContactUsTabComponent";
import CookiesPolicyTabComponent from "../../../Components/help-common-components/CookiesPolicyTabComponent";
import PrivacyPolicyTabComponent from "../../../Components/help-common-components/PrivacyPolicyTabComponent";
import TermsOfServiceTabComponent from "../../../Components/help-common-components/TermsOfServiceTabComponent";
import Header from "./Common/Header";
import { ScreenBGGradientView } from "../../../common/CommonView";

type HelpScreenRouteProp = RouteProp<{ params: { tab?: string } }, "params">;

export default function HelpScreen() {
  const styles = useStyles();
  const deviceSize = useDeviceSize();
  const navigation = useNavigation();
  const route = useRoute<HelpScreenRouteProp>();

  // Initialize from route params or URL query string
  const getInitialTab = () => {
    if (route.params?.tab) return route.params.tab;
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const tabFromUrl = urlParams.get('tab');
      if (tabFromUrl) return tabFromUrl;
    }
    return "gettingStarted";
  };

  const [selectedTab, setSelectedTab] = useState<string>(getInitialTab());
  const [menuVisible, setMenuVisible] = useState(false);

  // Fix URL every time screen comes into focus
  useFocusEffect(
    React.useCallback(() => {
      if (typeof window !== 'undefined') {
        const currentPath = window.location.pathname;
        const currentSearch = window.location.search;

        console.log(`[HelpScreen] Focus - currentPath: ${currentPath}, currentSearch: ${currentSearch}, selectedTab: ${selectedTab}`);

        // Fix capitalized or old screen name URLs
        if (currentPath === '/Help' || currentPath === '/HelpScreen') {
          const newUrl = `/help${currentSearch || `?tab=${selectedTab}`}`;
          window.history.replaceState(null, '', newUrl);
          console.log(`[HelpScreen] Fixed capitalized URL to: ${newUrl}`);
          return; // Exit early, URL will be corrected on next render
        }

        // Ensure we're on the help path and tab parameter is present
        if (currentPath === '/help' || currentPath === '/Help' || currentPath === '/HelpScreen') {
          const urlParams = new URLSearchParams(currentSearch);
          const tabFromUrl = urlParams.get('tab');

          if (!tabFromUrl) {
            const newUrl = `/help?tab=${selectedTab}`;
            window.history.replaceState(null, '', newUrl);
            console.log(`[HelpScreen] Added missing tab parameter: ${newUrl}`);
          }
        }
      }
    }, [selectedTab])
  );

  useEffect(() => {
    const tabFromUrl = route.params?.tab;
    if (tabFromUrl) {
      setSelectedTab(tabFromUrl);
    }
  }, [route.params]);

  const renderContent = () => {
    switch (selectedTab) {
      case "gettingStarted":
        return <GettingStartedTabComponent />;
      case "accountManagement":
        return <AccountManagementTabComponent />;
      case "tokenBelts":
        return <TokenBeltsCoinsTabComponent />;
      case "gifts":
        return <GiftTabComponent />;
      case "participation":
        return <EventParticipationTabComponent />;
      case "contentModeration":
        return <ContentModerationTabComponent />;
      case "disputeCenter":
        return <DisputeCenterTabComponent />;
      case "audienceInteraction":
        return <AudienceInteractionTabComponent />;
      case "refund":
        return <RefundTabComponent />;
      case "contactUs":
        return <ContactUsTabComponent />;
      case "termsOfService":
        return <TermsOfServiceTabComponent />;
      case "privacyPolicy":
        return <PrivacyPolicyTabComponent />;
      case "cookiesPolicy":
        return <CookiesPolicyTabComponent />;
      default:
        return <GettingStartedTabComponent />;
    }
  };

  useEffect(() => {
    if (selectedTab) {
      // Update URL manually to use /help instead of /HelpScreen
      if (typeof window !== 'undefined') {
        const newUrl = `/help?tab=${selectedTab}`;
        const currentUrl = window.location.pathname + window.location.search;
        // Only update if URL is different
        if (currentUrl !== newUrl) {
          window.history.replaceState(null, '', newUrl);
          console.log(`[HelpScreen] URL updated to: ${newUrl}`);
        }
      }
    }
  }, [selectedTab]);

  const tabs = [
    { key: "gettingStarted", label: "Getting Started" },
    { key: "accountManagement", label: "Account Management" },
    { key: "tokenBelts", label: "Using Tokens, Belts & Coins" },
    { key: "gifts", label: "Gifts" },
    { key: "participation", label: "Event Participation" },
    { key: "contentModeration", label: "Content Moderation" },
    { key: "termsOfService", label: "Terms of Service" },
    { key: "privacyPolicy", label: "Privacy Policy" },
    { key: "cookiesPolicy", label: "Cookies Policy" },
    { key: "disputeCenter", label: "Dispute Center" },
    { key: "audienceInteraction", label: "Audience Interaction" },
    { key: "refund", label: "Refund" },
    { key: "contactUs", label: "Contact Us" },
  ];
  const toggleMenu = () => {
    setMenuVisible(!menuVisible);
  };
  return (
    <View style={styles.container}>
      <ScreenBGGradientView />
      {/* Mobile Device navigation Header */}
      {/* {deviceSize === DEVICE_SIZES.XS ? (
        <MobileDeviceNavigationHeader
          btnNotificationTapped={() => {
            navigateToScreen(navigation, AppScreens.NotificationScreen);
          }}
          btnSettingTapped={() => {
            navigateToScreen(navigation, AppScreens.SettingWebScreen);
          }}
          btnSearchTapped={() => {
            navigateToScreen(navigation, AppScreens.SearchEventWebScreen);
          }}
        />
      ) : null} */}
      <Header
        btnSearchTapped={() => {
          navigateToScreen(navigation, AppScreens.SearchEventWebScreen);
        }}
        btnWalletTapped={() => {}}
        btnStartTutorialTapped={() => {}}
        btnMessageTapped={() => {}}
        btnNotificationTapped={() => {}}
        btnProfileTapped={() => {}}
      />

      <View style={styles.settingsContainer}>
        <Text style={[AppFontStyle.BOLD_ANYBODY_32, { marginBottom: 30 }]}>
          Help center
        </Text>

        <View style={styles.innerContainer}>
          {deviceSize === DEVICE_SIZES.SM || deviceSize === DEVICE_SIZES.XS ? (
            <View
              style={{
                backgroundColor: AppColors.gray_6E90B4,
                shadowColor: AppColors.purple_B155FF,
                shadowOffset: { width: 0, height: -4 },
                shadowOpacity: 0.8,
                shadowRadius: 4,
                elevation: 5,
                borderRadius: 71,
                padding: 10,
                paddingRight: 20,
                paddingLeft: 20,
                zIndex: 10,
                position: "relative",
              }}
            >
              <TouchableOpacity
                onPress={toggleMenu}
                style={{ flexDirection: "row", alignItems: "center", gap: 10 }}
              >
                <ImageComponent
                  url={AppImages.ThreeLines}
                  style={{
                    borderColor: AppColors.red_912121,
                    width: 42,
                    height: 22,
                  }}
                />
                <Text style={[AppFontStyle.BOLD_ANYBODY_14]}>Help center</Text>
              </TouchableOpacity>
              {menuVisible && (
                <View
                  style={{
                    position: "absolute",
                    top: 50,
                    zIndex: 20,
                    backgroundColor: AppColors.black,
                    borderRadius: 10,
                    padding: 10,
                    shadowColor: AppColors.gray_6E90B4,
                    shadowOffset: { width: 0, height: 4 },
                    shadowOpacity: 0.8,
                    shadowRadius: 4,
                    elevation: 5,
                    width:
                      deviceSize === DEVICE_SIZES.XS ||
                      deviceSize === DEVICE_SIZES.SM
                        ? "80%"
                        : "60%",
                  }}
                >
                  <ScrollView
                    style={{
                      maxHeight: 360,
                    }}
                  >
                    <HelpCenterMenuComponent
                      tabs={tabs}
                      selectedTab={selectedTab}
                      setSelectedTab={setSelectedTab}
                      styles={styles}
                      toggleMenu={toggleMenu}
                    />
                  </ScrollView>
                </View>
              )}
            </View>
          ) : (
            <View>
              <ScrollView
                style={{
                  maxHeight: 500,
                }}
                showsVerticalScrollIndicator={false}
              >
                <HelpCenterMenuComponent
                  tabs={tabs}
                  selectedTab={selectedTab}
                  setSelectedTab={setSelectedTab}
                  styles={styles}
                  toggleMenu={() => {}}
                />
              </ScrollView>
            </View>
          )}

          <ScrollView
            style={styles.contentContainer}
            showsVerticalScrollIndicator={true}
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.scrollContent}
          >
            {renderContent()}
          </ScrollView>
        </View>
      </View>
    </View>
  );
}

const useStyles = CreateResponsiveStyle(
  {
    container: {
      flex: 1,
    },
    settingsContainer: {
      backgroundColor: "transparent",
      paddingHorizontal: "3.5%",
      paddingTop: "2.5%",
      flex: 1,
    },
    innerContainer: {
      flex: 1,
      flexDirection: "row",
      gap: 50,
    },
    contentContainer: {
      flex: 1,
    },
    scrollContent: {
      paddingBottom: 50,
    },
    contentText: {
      color: AppColors.white,
      fontSize: 16,
    },
    buttonInner: {
      padding: 12,
    },
    buttonStyle: {
      borderColor: AppColors.grayColor_505050,
      borderWidth: 1,
      borderRadius: 10,
      width: "auto",
      marginBottom: 10,
    },
    topLineStyle: {
      width: "100%",
      height: 1,
      backgroundColor: AppColors.grayColor_505050,
    },

    buttonTextStyle: {
      marginTop: 7,
      paddingTop: 3,
      color: AppColors.white,
    },
  },
  {
    [DEVICE_SIZES.LG]: {},
    [DEVICE_SIZES.MD]: {},
    [DEVICE_SIZES.SM]: {
      innerContainer: {
        flexDirection: "column",
        gap: 30,
      },
    },
    [DEVICE_SIZES.XS]: {
      innerContainer: {
        flexDirection: "column",
        gap: 30,
      },
    },
  }
);
